import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { emailAttachmentManifestSchema } from "@west-tennessee-consulting/email-contracts";
import { normalizeTimelineStoredBody } from "@/lib/email/safe-email-body-utils";
import type {
  TimelineItem,
  TimelineFilters,
  UseEnhancedTimelineOptions,
  TicketUpdateQueryRow,
} from "./timeline-types";
import { ticketUpdateAuthor } from "./timeline-types";
import { applyFiltersSortAndLimit } from "./timeline-filter";
import { contactEmailMatchFilter } from "@/lib/utils/crm/contact-email-filter";

export { applyFiltersSortAndLimit };

/** Columns every communication-shaped timeline row needs. Shared by the
 * contact's own query and the inherited company-line query so the two produce
 * identical items apart from `scope`. */
const COMMUNICATION_SELECT = `
      id,
      title,
      body_preview,
      body_s3_key,
      attachments,
      attachment_count,
      direction,
      status,
      occurred_at,
      created_at,
      updated_at,
      user_id,
      contact_id,
      external_id,
      metadata,
      communication_types (
        key,
        label
      )
    `;

interface CommunicationRowLike {
  id: string;
  title: string | null;
  body_preview: string | null;
  body_s3_key: unknown;
  attachments: unknown;
  attachment_count: unknown;
  direction: string | null;
  status: string | null;
  occurred_at: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  contact_id: string;
  metadata: unknown;
  communication_types: unknown;
}

/**
 * The stored attachment manifest, as timeline items describe it.
 *
 * Validated with the SHARED `emailAttachmentManifestSchema` rather than trusted:
 * a row written by an older producer degrades to "none listed" instead of
 * throwing inside the timeline. No `url` — the bytes are not addressable from a
 * list row, so the row names the files and the detail surface fetches them.
 */
const toTimelineAttachments = (value: unknown): TimelineItem["attachments"] => {
  const parsed = emailAttachmentManifestSchema.safeParse(value);
  if (!parsed.success || parsed.data.length === 0) return undefined;
  return parsed.data.map((attachment) => ({
    name: attachment.filename,
    size: attachment.sizeBytes,
    contentType: attachment.contentType,
  }));
};

/** Map a communications row to a TimelineItem, tagging whose activity it is. */
const toCommunicationItem = (
  comm: CommunicationRowLike,
  scope: "contact" | "company",
  inheritedFrom?: TimelineItem["inherited_from"]
): TimelineItem => {
  const commType = comm.communication_types as { key: string; label: string } | null;
  return {
    id: comm.id,
    type: "communication" as const,
    title: comm.title || `${commType?.label || "Communication"}`,
    // List keeps the short preview after S3 offload. Detail modal hydrates
    // full HTML via body_s3_key → GET /api/communications/[id]/body.
    body: normalizeTimelineStoredBody(comm.body_preview ?? ""),
    body_s3_key:
      typeof comm.body_s3_key === "string" && comm.body_s3_key.trim()
        ? comm.body_s3_key.trim()
        : null,
    // Whether this message has files is a COLUMN, not a guess. Two renderers
    // used to hardcode `hasAttachments: false` here while the live-mailbox inbox
    // read the truth from the provider for the same email.
    attachment_count:
      typeof comm.attachment_count === "number" ? comm.attachment_count : 0,
    attachments: toTimelineAttachments(comm.attachments),
    occurred_at: comm.occurred_at,
    created_at: comm.created_at,
    updated_at: comm.updated_at,
    user_id: comm.user_id,
    contact_id: comm.contact_id,
    direction: comm.direction as TimelineItem["direction"],
    status: comm.status ?? undefined,
    metadata: comm.metadata as TimelineItem["metadata"],
    communication_type: commType || { key: "unknown", label: "Unknown" },
    scope,
    ...(inheritedFrom ? { inherited_from: inheritedFrom } : {}),
  };
};

const hasClientSideFilters = (filters?: TimelineFilters) =>
  Boolean(
    filters?.search ||
      filters?.type?.length ||
      filters?.dateFrom ||
      filters?.dateTo ||
      filters?.status?.length ||
      filters?.direction?.length
  );

const fetchTicketUpdatesTimeline = async (
  ticketId: string,
  options: UseEnhancedTimelineOptions = {}
): Promise<TimelineItem[]> => {
  const supabase = getBrowserSupabaseClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { data: ticketUpdates, error } = await supabase
    .from("ticket_updates")
    .select(
      `
      id,
      ticket_id,
      user_id,
      update_type,
      content,
      is_internal,
      created_at,
      app_users!ticket_updates_user_id_fkey(
        id,
        email,
        first_name,
        last_name
      )
    `
    )
    .eq("ticket_id", ticketId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching ticket updates:", error);
    throw new Error(error.message);
  }

  const timeline: TimelineItem[] = (ticketUpdates || []).map((update: TicketUpdateQueryRow) => {
    const author = ticketUpdateAuthor(update);
    const userName = author
      ? `${author.first_name || ""} ${author.last_name || ""}`.trim() || author.email
      : "Unknown User";
    // created_at is nullable in the generated row type; a ticket update always
    // has one in practice, so coalesce to satisfy the non-null TimelineItem shape.
    const createdAt = update.created_at ?? "";

    return {
      id: update.id,
      type: "ticket_update" as const,
      title: `${update.update_type.charAt(0).toUpperCase() + update.update_type.slice(1).replace("_", " ")} Update`,
      body: normalizeTimelineStoredBody(update.content ?? ""),
      occurred_at: createdAt,
      created_at: createdAt,
      updated_at: createdAt,
      user_id: update.user_id,
      contact_id: "",
      status: update.is_internal ? "internal" : "public",
      metadata: {
        type: "ticket_update",
        update_type: update.update_type,
        is_internal: update.is_internal,
        user_name: userName,
        user_email: author?.email,
        ticket_id: update.ticket_id,
      },
      communication_type: {
        key: `ticket_${update.update_type}`,
        label: `Ticket ${update.update_type.replace("_", " ")}`,
      },
    };
  });

  return applyFiltersSortAndLimit(timeline, options);
};

export const fetchEnhancedTimeline = async (
  email: string,
  options: UseEnhancedTimelineOptions = {}
): Promise<TimelineItem[]> => {
  if (options.ticketId) {
    return fetchTicketUpdatesTimeline(options.ticketId, options);
  }

  if (!email || typeof email !== "string" || email.trim() === "") {
    return [];
  }

  const supabase = getBrowserSupabaseClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  let resolvedContactId = options.contactId;
  if (!resolvedContactId) {
    const { data: contact, error: contactError } = await supabase
      .from("contacts")
      .select("id")
      .or(contactEmailMatchFilter(email.trim()))
      .single();

    if (contactError || !contact) return [];
    resolvedContactId = contact.id;
  }

  const timeline: TimelineItem[] = [];
  let mergedInheritedItems = false;
  const canApplyDatabaseLimit =
    Boolean(options.limit) &&
    !hasClientSideFilters(options.filters) &&
    (options.sort?.field ?? "occurred_at") === "occurred_at" &&
    (options.sort?.direction ?? "desc") === "desc";

  let communicationsQuery = supabase
    .from("communications")
    .select(COMMUNICATION_SELECT)
    .eq("contact_id", resolvedContactId)
    .order("occurred_at", { ascending: false });

  if (canApplyDatabaseLimit && options.limit) {
    communicationsQuery = communicationsQuery.limit(options.limit);
  }

  const { data: communications } = await communicationsQuery;

  if (communications) {
    timeline.push(
      ...(communications as unknown as CommunicationRowLike[]).map((comm) =>
        toCommunicationItem(comm, "contact")
      )
    );
  }

  // ---------------------------------------------------------------------------
  // Inherited company-line activity.
  // ---------------------------------------------------------------------------
  // A call to the front desk, or mail to info@, belongs to the COMPANY — it is
  // logged ONCE against the company's line-contact anchor rather than copied
  // onto every colleague's row (which is what manufactured duplicate "people"
  // out of one switchboard). The person still needs to see it, so we inherit it
  // here at the read layer and tag it `scope: "company"` for the UI to mark.
  //
  // Skipped when the viewed contact IS the anchor — its own query already
  // returned these rows, and inheriting them onto itself would double every item.
  if (!options.excludeCompanyActivity) {
    const { data: viewedContact } = await supabase
      .from("contacts")
      .select("company_id, is_company_line")
      .eq("id", resolvedContactId)
      .maybeSingle();

    if (viewedContact?.company_id && !viewedContact.is_company_line) {
      const { data: company } = await supabase
        .from("companies")
        .select("id, name, line_contact_id, organization_phone, organization_email")
        .eq("id", viewedContact.company_id)
        .maybeSingle();

      if (company?.line_contact_id) {
        let inheritedQuery = supabase
          .from("communications")
          .select(COMMUNICATION_SELECT)
          .eq("contact_id", company.line_contact_id)
          .order("occurred_at", { ascending: false });

        if (canApplyDatabaseLimit && options.limit) {
          inheritedQuery = inheritedQuery.limit(options.limit);
        }

        const { data: inherited } = await inheritedQuery;

        if (inherited) {
          const inheritedFrom: TimelineItem["inherited_from"] = {
            companyId: company.id,
            companyName: company.name ?? null,
            methodLabel: company.organization_phone ?? company.organization_email ?? null,
          };
          timeline.push(
            ...(inherited as unknown as CommunicationRowLike[]).map((comm) =>
              toCommunicationItem(comm, "company", inheritedFrom)
            )
          );
          // Two independently LIMITed queries were merged, so the database's
          // limit no longer describes the combined set — re-apply it client-side
          // or the caller gets up to 2× what it asked for.
          if (inherited.length > 0) mergedInheritedItems = true;
        }
      }
    }
  }

  // Attach open-tracking state (the "Opened" badge) for tracked emails. Reads
  // email_tracking_tokens by communication_id under the caller's RLS session.
  const communicationIds = timeline
    .filter((item) => item.type === "communication")
    .map((item) => item.id);
  if (communicationIds.length > 0) {
    const { data: trackingRows } = await supabase
      .from("email_tracking_tokens")
      .select("communication_id, opened_at, last_opened_at, open_count")
      .in("communication_id", communicationIds);

    if (trackingRows && trackingRows.length > 0) {
      // A communication can (rarely) have more than one token; fold them into a
      // single open state — opened if ANY token was opened, earliest first open.
      const byComm = new Map<string, TimelineItem["open_tracking"]>();
      for (const row of trackingRows) {
        if (!row.communication_id) continue;
        const prev = byComm.get(row.communication_id);
        const openedAt =
          [prev?.openedAt, row.opened_at]
            .filter((v): v is string => Boolean(v))
            .sort()[0] ?? null;
        const lastOpenedAt =
          [prev?.lastOpenedAt, row.last_opened_at]
            .filter((v): v is string => Boolean(v))
            .sort()
            .at(-1) ?? null;
        byComm.set(row.communication_id, {
          tracked: true,
          openedAt,
          lastOpenedAt,
          openCount: (prev?.openCount ?? 0) + (row.open_count ?? 0),
        });
      }
      for (const item of timeline) {
        const state = byComm.get(item.id);
        if (state) item.open_tracking = state;
      }
    }
  }

  return applyFiltersSortAndLimit(
    timeline,
    options,
    canApplyDatabaseLimit && !mergedInheritedItems
  );
};
